import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GoogleLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [submitting, setSubmitting] = useState(false);
  const { user, login, register, googleLogin } = useAuth();
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        if (!form.name.trim()) { toast.error('Name is required'); setSubmitting(false); return; }
        await register(form.email, form.password, form.name);
      }
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const update = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1699058455521-2d3d52e04861?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxnb2xmZXIlMjBzd2luZyUyMHN1bnNldHxlbnwwfHx8fDE3NzU0MzY3NDV8MA&ixlib=rb-4.1.0&q=85"
          alt="Golfer at sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B421A]/90 via-[#0B421A]/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-16 pb-24">
          <h1 className="font-serif text-6xl text-white font-semibold tracking-tight leading-none">
            GolfScroll
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-md leading-relaxed font-sans">
            Share your game. Connect with golfers worldwide. Elevate your play.
          </p>
          <div className="mt-8 flex items-center gap-6">
            <div className="h-px flex-1 bg-white/20" />
            <span className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-sans font-semibold">The Social Club</span>
            <div className="h-px flex-1 bg-white/20" />
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center p-8 sm:p-12 bg-background">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden text-center">
            <h1 className="font-serif text-4xl text-primary font-semibold tracking-tight">GolfScroll</h1>
            <p className="text-muted-foreground text-sm mt-2 font-sans">Share your game</p>
          </div>

          <div className="flex border-b border-border" data-testid="auth-tabs">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 pb-3 text-sm font-semibold tracking-wide uppercase font-sans transition-colors duration-300 ${mode === 'login' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="auth-tab-login"
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 pb-3 text-sm font-semibold tracking-wide uppercase font-sans transition-colors duration-300 ${mode === 'register' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="auth-tab-register"
            >
              Create Account
            </button>
          </div>

          <Button
            variant="outline"
            onClick={googleLogin}
            className="w-full h-12 gap-3 rounded-sm border-border text-foreground hover:bg-muted transition-colors duration-300"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-4 text-muted-foreground tracking-widest font-sans">or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Name</Label>
                <Input
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Your full name"
                  className="h-12 rounded-sm bg-transparent border-0 border-b border-border focus:border-primary focus-visible:ring-0 shadow-none px-0"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="your@email.com"
                required
                className="h-12 rounded-sm bg-transparent border-0 border-b border-border focus:border-primary focus-visible:ring-0 shadow-none px-0"
                data-testid="auth-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={update('password')}
                placeholder="Min 6 characters"
                required
                minLength={6}
                className="h-12 rounded-sm bg-transparent border-0 border-b border-border focus:border-primary focus-visible:ring-0 shadow-none px-0"
                data-testid="auth-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-sm bg-primary hover:bg-[#083313] transition-colors duration-300 text-sm tracking-wide uppercase font-semibold font-sans"
              data-testid="auth-submit-btn"
            >
              {submitting ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
